import {
  CardListShimmer,
  EntityList,
  EntityListNavigationType,
  SVGIconComponent,
  useInMemoryFilteredList
} from '@hevoio/hevo-ui-toolkit';

import { useCallback, useRef, useState } from 'react';
import { IconNetwork } from '@tabler/icons-react';
import { ConnectivityCards } from './components/ConnectivityCards';
import { CreateVPCPeeringButton } from './components/CreateVPCPeeringButton';
import { CreateVPCModal } from './components/CreateVPCModal/CreateVPCModal';
import TeamAPI from '../api/TeamAPI';
import { VPCPeeringConnection } from './interface';
import { RbacPermissions } from '../../../core/interfaces/user';
import useHasPermission from '../../../access-control/hooks/useHasPermission';
import useUserService from '../../../auth/hooks/useUserService';

export function Connectivity() {
  const [openCreateVPCModal, setOpenCreateVPCModal] = useState(false);
  const { user } = useUserService();
  const entityListRef = useRef<{ reset: () => void }>();

  const listApi = useInMemoryFilteredList<VPCPeeringConnection>(
    () =>
      TeamAPI.getConnectivityPeeringList(user.teamId).then(
        (res: { total_count: number; data: VPCPeeringConnection[] }) => res.data
      ),
    () => true
  );
  const { fetch } = listApi;

  // useInMemoryFilteredList hands back a fresh identity for every function on
  // every render, so hold the whole thing in a ref and expose stable callbacks
  // — anything derived from it directly would re-trigger every effect and memo
  // it is passed to.
  const listApiRef = useRef(listApi);
  listApiRef.current = listApi;

  const { hasPermission } = useHasPermission();

  // A full reset drops the list into its LOADING state, which unmounts the list
  // component. That is fine after a create/edit/delete — the user is done with
  // the form — but never for the background poll, which patches rows in place.
  const onActionComplete = useCallback(() => {
    listApiRef.current.reset();
    entityListRef.current?.reset();
  }, []);

  const updateMemoryData = useCallback((data: VPCPeeringConnection[] | undefined) => {
    listApiRef.current.updateMemoryData(data);
  }, []);

  return (
    <>
      <CreateVPCModal
        open={openCreateVPCModal}
        onClose={() => setOpenCreateVPCModal(false)}
        onSuccess={onActionComplete}
      />
      <EntityList
        innerRef={entityListRef}
        hideRefresh
        mode='standalone'
        flat
        entityTitle='VPC Peering Connections'
        navigationType={EntityListNavigationType.INFINITE_SCROLL}
        newListSVGIcon={IconNetwork as unknown as SVGIconComponent}
        newListConnectedTitle='Set up a VPC Peering connection'
        newListConnectedSubTitle='A VPC Peering connection establishes a private network connection between your AWS Source or Destination database and Hevo, enabling secure access without exposing the database to the public internet.'
        newListComponentProps={{
          primaryCTAProps: {
            dataId: 'create-vpc-peering',
            icon: 'plus',
            className: 'px-3 py-2',
            disabled: !hasPermission(RbacPermissions.PIPELINE_CREATE),
            onClick: () => {
              setOpenCreateVPCModal(true);
            },
            size: 'md',
            children: <>Create VPC Peering</>
          }
        }}
        genericErrorTitle=''
        genericErrorSubTitle=''
        ListComponent={ConnectivityCards}
        listComponentProps={{ onActionComplete, updateMemoryData }}
        ShimmerComponent={CardListShimmer}
        emptyTitle=''
        emptySubTitle=''
        hidePagination
        dataSource$={fetch}
        entity='vpc-connections'
        StandaloneModeActions={() => (
          <CreateVPCPeeringButton
            onClick={() => {
              setOpenCreateVPCModal(true);
            }}
          />
        )}
      />
    </>
  );
}
